import { useId, useState, type FormEvent, type ReactNode } from "react";
import { RequestError } from "@/components/RequestError";
import { Button, type ButtonVariant } from "@/components/ui/button";
import { TextAreaField } from "@/components/ui/field";
import { useSubmitState } from "@/lib/use-form";

type ReasonFormProps = {
  label: string;
  hint?: string;
  submitLabel: string;
  submitVariant?: ButtonVariant;
  onSubmit: (reason: string) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
  children?: ReactNode;
};

/** A confirmation that needs a written reason (cancelling, regenerating a PDF). */
export function ReasonForm({
  label,
  hint,
  submitLabel,
  submitVariant = "primary",
  onSubmit,
  onCancel,
  onReload,
  children,
}: ReasonFormProps) {
  const id = useId();
  const [reason, setReason] = useState("");
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() => onSubmit(reason.trim()));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      {children}
      <RequestError error={error} onReload={onReload} />
      <TextAreaField
        id={`${id}-reason`}
        label={label}
        value={reason}
        onChange={setReason}
        error={errors.reason}
        hint={hint}
        rows={2}
      />
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Back
        </Button>
        <Button type="submit" variant={submitVariant} disabled={saving || reason.trim().length < 3}>
          {saving ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}
